import InvoiceTemplate from '../components/BillTemplates/InvoiceTemplate';
import InvoiceTemplateA5 from '../components/BillTemplates/InvoiceTemplateA5';
import SimpleSalesTemplate from '../components/BillTemplates/SimpleSalesTemplate';
import ModernSalesTemplate from '../components/BillTemplates/ModernSalesTemplate';
import ChallanTemplate from '../components/BillTemplates/ChallanTemplate';
import ChallanTemplateA5 from '../components/BillTemplates/ChallanTemplateA5';
import QuotationTemplate from '../components/BillTemplates/QuotationTemplate';
import QuotationTemplateColored from '../components/BillTemplates/QuotationTemplateColored';
import { findItemInMaster, getItemDisplayName, getBillLineItems } from './itemDisplayUtils';

export const LAYOUT_DOC_TYPES = [
  { key: 'invoice', label: 'Invoice', numberLabel: 'Invoice Number', defaultTemplate: 'InvoiceTemplate' },
  { key: 'quotation', label: 'Quotation', numberLabel: 'Quotation Number', defaultTemplate: 'QuotationTemplate' },
  { key: 'challan', label: 'Challan', numberLabel: 'Challan Number', defaultTemplate: 'ChallanTemplate' },
];

/** Firestore sources for syncing an existing bill into the layout editor (read-only). */
export const LAYOUT_BILL_SOURCES = [
  { key: 'invoice', label: 'Invoices', collection: 'salesBills', layoutDocType: 'invoice' },
  { key: 'quotation', label: 'Quotations', collection: 'quotations', layoutDocType: 'quotation' },
  { key: 'challan', label: 'Challans', collection: 'challans', layoutDocType: 'challan' },
];

export const LAYOUT_TEMPLATES = [
  { key: 'InvoiceTemplate', label: 'Invoice — Standard A4', docTypes: ['invoice'], component: InvoiceTemplate },
  { key: 'InvoiceTemplateA5', label: 'Invoice — Compact A5', docTypes: ['invoice'], component: InvoiceTemplateA5 },
  { key: 'SimpleSalesTemplate', label: 'Invoice — Simple', docTypes: ['invoice'], component: SimpleSalesTemplate },
  { key: 'ModernSalesTemplate', label: 'Invoice — Modern', docTypes: ['invoice'], component: ModernSalesTemplate },
  { key: 'QuotationTemplate', label: 'Quotation — Standard', docTypes: ['quotation'], component: QuotationTemplate },
  { key: 'QuotationTemplateColored', label: 'Quotation — Colored', docTypes: ['quotation'], component: QuotationTemplateColored },
  { key: 'ChallanTemplate', label: 'Challan — Standard A4', docTypes: ['challan'], component: ChallanTemplate },
  { key: 'ChallanTemplateA5', label: 'Challan — Compact A5', docTypes: ['challan'], component: ChallanTemplateA5 },
];

const DRAFT_STORAGE_PREFIX = 'layoutDocumentDraft_';

export function getTemplatesForDocType(docType) {
  return LAYOUT_TEMPLATES.filter((t) => t.docTypes.includes(docType));
}

export function resolveTemplateComponent(templateKey, docType) {
  const match = LAYOUT_TEMPLATES.find((t) => t.key === templateKey);
  if (match) return match.component;
  const fallback = LAYOUT_DOC_TYPES.find((d) => d.key === docType);
  const defaultKey = fallback?.defaultTemplate;
  return LAYOUT_TEMPLATES.find((t) => t.key === defaultKey)?.component || InvoiceTemplate;
}

export function getDefaultTemplateForDocType(docType) {
  const savedDefaults = typeof localStorage !== 'undefined' ? localStorage.getItem('defaultBillTemplates') : null;
  if (savedDefaults) {
    try {
      const parsed = JSON.parse(savedDefaults);
      const key = parsed[docType];
      if (key && LAYOUT_TEMPLATES.some((t) => t.key === key && t.docTypes.includes(docType))) {
        return key;
      }
    } catch (_) {
      /* ignore */
    }
  }
  return LAYOUT_DOC_TYPES.find((d) => d.key === docType)?.defaultTemplate || 'InvoiceTemplate';
}

export function createEmptyRow() {
  return {
    id: `row_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    description: '',
    hsn: '',
    qty: '',
    rate: '',
    amount: '',
    sgst: '',
    cgst: '',
    igst: '',
  };
}

export function createDefaultLayoutState(overrides = {}) {
  const today = new Date().toISOString().split('T')[0];
  return {
    docType: 'invoice',
    templateKey: getDefaultTemplateForDocType('invoice'),
    documentNumber: '',
    documentDate: today,
    partyName: '',
    partyAddress: '',
    partyGstin: '',
    partyContact: '',
    partyState: '',
    notes: '',
    discountType: 'percent',
    discountValue: '',
    showWatermark: true,
    rows: [createEmptyRow(), createEmptyRow(), createEmptyRow()],
    ...overrides,
  };
}

export function recalculateRowAmount(row) {
  const qty = parseFloat(row.qty) || 0;
  const rate = parseFloat(row.rate) || 0;
  if (qty > 0 && rate > 0) {
    return { ...row, amount: String(Math.round(qty * rate * 100) / 100) };
  }
  return row;
}

export function buildPartyDetailsFromLayout(state) {
  return {
    name: state.partyName || '',
    firmName: state.partyName || '',
    address: state.partyAddress || '',
    gstin: state.partyGstin || '',
    contact: state.partyContact || '',
    contactNumber: state.partyContact || '',
    state: state.partyState || '',
  };
}

export function buildBillDataFromLayout(state) {
  const items = (state.rows || [])
    .map(recalculateRowAmount)
    .filter((row) => {
      const hasDesc = !!(row.description && String(row.description).trim());
      const hasNumbers =
        (parseFloat(row.qty) || 0) > 0 ||
        (parseFloat(row.rate) || 0) > 0 ||
        (parseFloat(row.amount) || 0) > 0;
      return hasDesc || hasNumbers;
    });

  const base = {
    items,
    rows: items,
    notes: state.notes || '',
    discountType: state.discountType || 'percent',
    discountValue: state.discountValue || '',
    paymentStatus: 'Unpaid',
    totalPaid: 0,
  };

  if (state.docType === 'quotation') {
    return {
      ...base,
      quotationNumber: state.documentNumber || '',
      number: state.documentNumber || '',
      date: state.documentDate || '',
    };
  }

  if (state.docType === 'challan') {
    return {
      ...base,
      challanNumber: state.documentNumber || '',
      number: state.documentNumber || '',
      challanDate: state.documentDate || '',
      date: state.documentDate || '',
    };
  }

  return {
    ...base,
    invoiceNumber: state.documentNumber || '',
    number: state.documentNumber || '',
    invoiceDate: state.documentDate || '',
    date: state.documentDate || '',
  };
}

export function saveLayoutDraft(companyId, state) {
  if (!companyId || typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(`${DRAFT_STORAGE_PREFIX}${companyId}`, JSON.stringify(state));
  } catch (e) {
    console.warn('Failed to save layout document draft:', e);
  }
}

export function loadLayoutDraft(companyId) {
  if (!companyId || typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(`${DRAFT_STORAGE_PREFIX}${companyId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      ...createDefaultLayoutState(),
      ...parsed,
      rows: Array.isArray(parsed.rows) && parsed.rows.length ? parsed.rows : createDefaultLayoutState().rows,
    };
  } catch (e) {
    console.warn('Failed to load layout document draft:', e);
    return null;
  }
}

export function clearLayoutDraft(companyId) {
  if (!companyId || typeof localStorage === 'undefined') return;
  localStorage.removeItem(`${DRAFT_STORAGE_PREFIX}${companyId}`);
}

function normalizeBillDateForInput(bill) {
  const raw = bill?.invoiceDate || bill?.date || bill?.billDate || bill?.challanDate || '';
  if (!raw) return new Date().toISOString().split('T')[0];
  if (typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  try {
    const d = typeof raw?.toDate === 'function' ? raw.toDate() : new Date(raw);
    if (!Number.isNaN(d.getTime())) return d.toISOString().split('T')[0];
  } catch (_) {
    /* ignore */
  }
  return new Date().toISOString().split('T')[0];
}

export function mapBillRowToLayoutRow(row, items = []) {
  if (!row || typeof row !== 'object') return createEmptyRow();
  const itemMaster = findItemInMaster(row.item || row.itemId, items);
  const layoutRow = createEmptyRow();
  return {
    ...layoutRow,
    description:
      row.description ||
      row.desc ||
      getItemDisplayName(itemMaster) ||
      row.itemName ||
      row.name ||
      (typeof row.item === 'string' && !row.item.includes('/') ? row.item : '') ||
      '',
    hsn: row.hsn || row.hsnCode || itemMaster?.hsnCode || itemMaster?.hsn || '',
    qty: row.qty != null && row.qty !== '' ? String(row.qty) : row.nos != null ? String(row.nos) : '',
    rate: row.rate != null && row.rate !== '' ? String(row.rate) : '',
    amount: row.amount != null && row.amount !== '' ? String(row.amount) : '',
    sgst: row.sgst != null && row.sgst !== '' ? String(row.sgst) : '',
    cgst: row.cgst != null && row.cgst !== '' ? String(row.cgst) : '',
    igst: row.igst != null && row.igst !== '' ? String(row.igst) : '',
  };
}

/**
 * Copy fields from a saved sales document into layout editor state.
 * Does not write anything back to Firestore.
 */
export function mapBillToLayoutState(
  bill,
  { parties = [], items = [], sourceKey = 'invoice', preserve = {} } = {}
) {
  const source = LAYOUT_BILL_SOURCES.find((s) => s.key === sourceKey) || LAYOUT_BILL_SOURCES[0];
  const party = parties.find((p) => p.id === bill.party) || {};
  const billRows = getBillLineItems(bill);
  const rows = billRows.length ? billRows.map((row) => mapBillRowToLayoutRow(row, items)) : [createEmptyRow()];
  const layoutDocType = source.layoutDocType || sourceKey;

  return {
    ...createDefaultLayoutState(),
    docType: layoutDocType,
    templateKey: preserve.templateKey || getDefaultTemplateForDocType(layoutDocType),
    showWatermark: preserve.showWatermark !== undefined ? preserve.showWatermark : true,
    documentNumber: bill.number || bill.invoiceNumber || bill.quotationNumber || bill.challanNumber || '',
    documentDate: normalizeBillDateForInput(bill),
    partyName: party.firmName || party.name || bill.partyName || '',
    partyAddress: party.address || bill.partyAddress || '',
    partyGstin: party.gstin || party.gstNumber || bill.partyGstin || '',
    partyContact: party.contactNumber || party.phone || bill.partyContact || '',
    partyState: party.state || bill.partyState || '',
    notes: bill.notes || '',
    discountType: bill.discountType || 'percent',
    discountValue: bill.discountValue != null && bill.discountValue !== '' ? String(bill.discountValue) : '',
    rows,
    syncedFromBillId: bill.id || '',
    syncedFromSource: sourceKey,
    syncedFromNumber: bill.number || '',
  };
}

function parseBillDateValue(bill) {
  const raw = bill?.invoiceDate || bill?.date || bill?.billDate || bill?.challanDate || '';
  if (!raw) return null;
  try {
    const d = typeof raw?.toDate === 'function' ? raw.toDate() : new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
  } catch (_) {
    return null;
  }
}

export function getBillPartyName(bill, parties = []) {
  const party = parties.find((p) => p.id === bill?.party);
  return party?.firmName || party?.name || bill?.partyName || '—';
}

export function getBillDisplayDate(bill) {
  const d = parseBillDateValue(bill);
  if (!d) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function getBillDisplayAmount(bill) {
  const amount = bill?.grandTotal ?? bill?.totalAmount ?? bill?.amount;
  if (amount == null || amount === '') return '—';
  const n = Number(amount);
  return Number.isFinite(n) ? `₹${n.toLocaleString('en-IN')}` : String(amount);
}

export function formatBillOptionLabel(bill, parties = []) {
  const partyName = getBillPartyName(bill, parties);
  const num = bill.number || '—';
  const dateStr = getBillDisplayDate(bill);
  const amtStr = getBillDisplayAmount(bill);
  const amtPart = amtStr !== '—' ? ` · ${amtStr}` : '';
  return `${num} · ${partyName}${dateStr !== '—' ? ` · ${dateStr}` : ''}${amtPart}`;
}

export function filterBillsForSync(
  bills,
  {
    search = '',
    billNumber = '',
    fromDate = '',
    toDate = '',
    parties = [],
    recentLimit = 25,
    matchLimit = 100,
  } = {}
) {
  const searchTerms = String(search || '')
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
  const exactNum = String(billNumber || '').trim().toLowerCase();
  const hasFilters = searchTerms.length > 0 || exactNum || fromDate || toDate;

  let list = [...(bills || [])];

  if (exactNum) {
    list = list.filter((bill) => String(bill.number || '').toLowerCase().includes(exactNum));
  }

  if (searchTerms.length) {
    list = list.filter((bill) => {
      const partyName = getBillPartyName(bill, parties).toLowerCase();
      const num = String(bill.number || '').toLowerCase();
      return searchTerms.every((term) => num.includes(term) || partyName.includes(term));
    });
  }

  if (fromDate) {
    const from = new Date(`${fromDate}T00:00:00`);
    list = list.filter((bill) => {
      const d = parseBillDateValue(bill);
      return d && d >= from;
    });
  }

  if (toDate) {
    const to = new Date(`${toDate}T23:59:59`);
    list = list.filter((bill) => {
      const d = parseBillDateValue(bill);
      return d && d <= to;
    });
  }

  const sorted = list.sort((a, b) => {
    const dateA = parseBillDateValue(a) || new Date(0);
    const dateB = parseBillDateValue(b) || new Date(0);
    return dateB - dateA;
  });

  const limit = hasFilters ? matchLimit : recentLimit;
  return {
    bills: sorted.slice(0, limit),
    totalMatches: sorted.length,
    isFiltered: hasFilters,
  };
}

/** Prefer exact number match, then single-result, then first partial match. */
export function pickBillFromLookup(bills, billNumber, parties = []) {
  const q = String(billNumber || '').trim().toLowerCase();
  if (!q) return null;
  const { bills: matches } = filterBillsForSync(bills, { billNumber: q, parties, matchLimit: 50 });
  if (!matches.length) return null;
  const exact = matches.find((b) => String(b.number || '').toLowerCase() === q);
  if (exact) return exact;
  if (matches.length === 1) return matches[0];
  return matches[0];
}
